// Sizes rendered phone shots for display. Each shot is a native-size stage inside
// a `.thumb`; this scales the stage down to fill the thumb's column and crops it
// the way its step asked. It is the only runtime the Rails help center needs, so
// it carries no preview chrome (see preview.js for that).

export function layoutThumbs() {
  document.querySelectorAll(".thumb").forEach((thumb) => {
    const frame = thumb.querySelector(".stage") ?? thumb.querySelector(".phone");
    if (!frame) return;

    // Size the crop. A step either names a slice height or asks to be framed on
    // its highlight. Either way the shot is anchored to the phone top and chops
    // whatever falls below, and never shows dead space past the screen.
    if (frame.classList.contains("stage")) {
      const screen = frame.querySelector(".screen");
      if (screen) {
        const body = screen.querySelector(".screen-body");
        const scrollRegion = screen.querySelector(".scroll-region");
        const scroller = scrollRegion || body;

        // A previous pass may have pushed the phone up for a bottom crop. Reset it
        // so this pass measures the phone from its true top.
        frame.style.removeProperty("--phone-top");

        // The natural content height, for the content-fit fallback. Neutralize the
        // flex children that would otherwise collapse so the screen grows to fit.
        const grown = [body, scrollRegion].filter(Boolean);
        const savedFlex = grown.map((el) => el.style.flex);
        grown.forEach((el) => {
          el.style.flex = "none";
        });
        screen.style.height = "auto";
        const content = screen.offsetHeight;
        screen.style.height = "";
        grown.forEach((el, i) => {
          el.style.flex = savedFlex[i];
        });

        const screenH = screen.offsetHeight;
        const phoneTop = parseFloat(getComputedStyle(frame).getPropertyValue("--phone-top")) || 24;
        const pad = 12;
        const margin = 40;
        const fullPhone = phoneTop + pad + screenH + pad;
        const crop = frame.getAttribute("data-crop");

        if (scroller) scroller.scrollTop = 0;
        // The header lives outside the scroller, so a highlight there (a header
        // action) never drives the crop; only highlights inside the scroller do.
        const taps = crop === "highlight" && scroller ? [...scroller.querySelectorAll(".tap")] : [];

        let stageH;
        if (crop === "bottom") {
          // These actions live at the foot of a long screen. Scroll the content to
          // its bottom so they sit in view, then take a slice anchored to the
          // phone's bottom edge, letting the top (header and earlier cards) run off
          // above the shot so the actions read as end-of-screen.
          if (scroller) scroller.scrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
          stageH = Math.min(fullPhone, 660);
          frame.style.setProperty("--phone-top", `${stageH - (fullPhone - phoneTop)}px`);
        } else if (taps.length) {
          // Offset of the scroller's top within the screen, plus the lowest
          // highlighted edge inside it, is where the shot should end.
          const aboveScroller = scroller.offsetTop + (scroller === scrollRegion && body ? body.offsetTop : 0);
          // A docked footer covers the foot of the scroller, so a highlight has to
          // clear it to be seen. The room it takes comes off what counts as visible.
          const footer = screen.querySelector(":scope > .screen-footer");
          const visibleH = scroller.clientHeight - (footer ? footer.offsetHeight : 0);
          const hlBottom = taps.reduce((max, t) => Math.max(max, t.offsetTop + t.offsetHeight), 0);
          let contentBottom;
          if (hlBottom + margin <= visibleH) {
            contentBottom = aboveScroller + hlBottom;
          } else {
            // The highlight sits more than a screen down, so scroll it up until it
            // lands near the foot of the shot, keeping as much above it as fits.
            scroller.scrollTop = hlBottom + margin - visibleH;
            contentBottom = aboveScroller + scroller.clientHeight;
          }
          stageH = Math.min(fullPhone, phoneTop + pad + contentBottom + margin);
        } else {
          // No highlight to frame on: fit to the content, but show the whole phone
          // for a screen that carries a bottom sheet or a docked footer (each sits
          // at the foot).
          const visible = screen.querySelector(":scope > .sheet, :scope > .keypad, :scope > .screen-footer")
            ? screenH
            : Math.min(content, screenH);
          const fit = phoneTop + pad + visible + pad;
          const requested = crop != null && crop !== "highlight" ? parseFloat(crop) || fit : fit;
          stageH = Math.min(requested, fit);
          const raw = scroller && scroller.getAttribute("data-scroll");
          if (raw != null) scroller.scrollTop = raw === "highlight" ? 0 : parseFloat(raw) || 0;
        }
        frame.style.setProperty("--stage-h", `${stageH}px`);
      }
    }

    // The figure fills its column up to a max, so the shot never overflows on a
    // narrow screen and caps its width on a wide one.
    const figure = thumb.closest("figure");
    if (figure) {
      figure.style.width = "100%";
      figure.style.maxWidth = `${parseFloat(thumb.getAttribute("data-thumb-width") ?? "560")}px`;
    }
    const target = thumb.clientWidth;
    if (!target) return;
    const scale = target / frame.offsetWidth;
    frame.style.transformOrigin = "top left";
    frame.style.transform = `scale(${scale})`;
    thumb.style.height = `${frame.offsetHeight * scale}px`;
  });
}

let resizeBound = false;

// Sizes every shot now and keeps them sized as the viewport changes. Safe to call
// again after more shots are injected; the resize listener binds once.
export function fitThumbs() {
  layoutThumbs();
  if (!resizeBound) {
    window.addEventListener("resize", layoutThumbs);
    resizeBound = true;
  }
}
